import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resetPasswordWithToken, verifyResetToken } from "@services/auth";

const EyeIcon = () => (
  <svg
    className="w-4 h-4 text-gray-800 dark:text-white"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="currentColor"
      strokeWidth="2"
      d="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Z"
    />
    <path
      stroke="currentColor"
      strokeWidth="2"
      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
    />
  </svg>
);

const EyeSlashIcon = () => (
  <svg
    className="w-4 h-4 text-gray-800 dark:text-white"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3.933 13.909A4.357 4.357 0 0 1 3 12c0-1 4-6 9-6m7.6 3.8A5.068 5.068 0 0 1 21 12c0 1-3 6-9 6-.314 0-.62-.014-.918-.04M5 19 19 5m-4 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
    />
  </svg>
);
function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function ResetPassword() {
  const query = useQuery();
  const navigate = useNavigate();
  const tokenFromUrl = query.get("token");

  const [token, setToken] = useState(null);
  const [checkingToken, setCheckingToken] = useState(true);
  const [tokenStatus, setTokenStatus] = useState("checking"); 
  // "checking" | "valid" | "expired" | "invalid"

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!tokenFromUrl) {
      setTokenStatus("invalid");
      setCheckingToken(false);
      return;
    }

    setToken(tokenFromUrl);

    // Hide token from the address bar
    window.history.replaceState({}, document.title, window.location.pathname);

    // Check validity with backend
    const runCheck = async () => {
      try {
        await verifyResetToken({ token: tokenFromUrl });
        setTokenStatus("valid");
      } catch (err) {
        const msg = err?.response?.data?.message || "";
        if (msg.toLowerCase().includes("expired")) {
          setTokenStatus("expired");
        } else {
          setTokenStatus("invalid");
        }
      } finally {
        setCheckingToken(false);
      }
    };

    runCheck();
  }, [tokenFromUrl]);

  const isValidPassword = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~])[A-Za-z\d!"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~]{8,}$/.test(
      password
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!token) {
      setErrorMessage("Reset link is invalid or missing.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    if (!isValidPassword(password)) {
      setErrorMessage(
        "Password must be at least 8 characters and include upper, lower, number, and special character."
      );
      return;
    }

    try {
      setLoading(true);
      const res = await resetPasswordWithToken({
        token,
        password,
        confirm_password: confirmPassword,
      });

      setSuccessMessage(
        res?.message || "Password reset successfully. Redirecting to login..."
      );
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      console.error("Reset password error", err);
      const msg = err?.response?.data?.message || "";

      if (msg.toLowerCase().includes("expired")) {
        setTokenStatus("expired");
        setErrorMessage("");
      } else {
        setErrorMessage(
          msg || "Reset link is invalid, expired, or something went wrong."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingToken) {
    return (
      <div className="text-center py-10">
        <div className="w-10 h-10 mx-auto border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500 mt-3">Verifying link...</p>
      </div>
    );
  }

  if (tokenStatus === "invalid") {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Invalid Link</h2>
        <p className="text-sm text-gray-500 mt-2">
          The password reset link is invalid. Please request a new one.
        </p>
        <button
          onClick={() => navigate("/forgot-password")}
          className="mt-4 bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 rounded-lg"
        >
          Request New Reset Link
        </button>
      </div>
    );
  }

  if (tokenStatus === "expired") {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Link Expired</h2>
        <p className="text-sm text-gray-500 mt-2">
          This password reset link has expired. Please request a new one.
        </p>
        <button
          onClick={() => navigate("/forgot-password")}
          className="mt-4 bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 rounded-lg"
        >
          Request New Reset Link
        </button>
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-80 z-50">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">
          Reset your password
        </h2>
        <p className="text-sm text-gray-500">
          Choose a strong password to keep your account secure.
        </p>
      </div>

      <form className="space-y-4 text-left mt-4" onSubmit={handleSubmit}>
        {/* New Password */}
        <div>
          <label
            htmlFor="password"
            className="block mb-1 text-sm font-medium text-gray-900"
          >
            New Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-600"
            >
              {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </button>
          </div>
        </div>

        {/* Confirm New Password */}
        <div>
          <label
            htmlFor="confirmPassword"
            className="block mb-1 text-sm font-medium text-gray-900"
          >
            Confirm New Password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
            <button
              type="button"
              onClick={() =>
                setShowConfirmPassword((prev) => !prev)
              }
              className="absolute inset-y-0 right-3 flex items-center text-gray-600"
            >
              {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </button>
          </div>
        </div>

        {errorMessage && (
          <p className="text-sm text-red-600 px-2">{errorMessage}</p>
        )}
        {successMessage && (
          <p className="text-sm text-green-600 px-2">{successMessage}</p>
        )}

        <button
          type="submit"
          className="w-full bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 rounded-lg"
        >
          Reset Password
        </button>

        <button
          type="button"
          onClick={() => navigate("/login")}
          className="w-full mt-2 text-sm text-blue-700 hover:underline"
        >
          Back to Login
        </button>
      </form>
    </>
  );
}
